import { useInviteConsultee, usePermissions } from '../../../api';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  inviteEmailState,
  inviteNameState,
  invitePermissionState,
  inviteRequestState,
  inviteTeamsState,
  showInviteUserModalState,
  useResetInviteUserState,
} from '../../../store';
import { Permission } from '../../../types';
import { isEmailValid, isNameValid } from '../../../tools';
import { isNumeric } from '@tq/ui-components';
import { UseInviteUserFormReturn } from '../fragments/UseInviteUserFormReturn';
import { PageTypeProps } from '../fragments/ManageTeamInviteUserProps';

export type useInviteUserFormProps = PageTypeProps & Record<string, unknown>;

export const UseInviteUserForm = ({
  pageType,
}: useInviteUserFormProps): UseInviteUserFormReturn => {
  const { data: permissionData } = usePermissions();
  const [emailIsInvalid, setEmailIsInvalid] = useState<boolean>(false);
  const [nameIsInvalid, setNameIsInvalid] = useState<boolean>(false);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useRecoilState(inviteEmailState);
  const [inviteName, setInviteName] = useRecoilState(inviteNameState);
  const [inviteTeams] = useRecoilState(inviteTeamsState);
  const [invitePermission, setInvitePermission] = useRecoilState(invitePermissionState);
  const inviteRequest = useRecoilValue(inviteRequestState);
  const setShowInviteUserModal = useSetRecoilState(showInviteUserModalState);

  const { mutate: inviteConsultee } = useInviteConsultee();
  const resetInviteState = useResetInviteUserState();
  const isManageAdminUsersPage = pageType === 'ManageTQAdmin';
  const _adminPermissionId = permissionData?.permissions?.find(
    (o) => o.label === Permission.TQAdmin
  )?.value;

  useEffect(() => {
    if (isManageAdminUsersPage && _adminPermissionId) {
      setInvitePermission(_adminPermissionId);
    }
  });

  useEffect(() => {
    if (isManageAdminUsersPage) {
      setCanSubmit(
        isNameValid(inviteName) && isEmailValid(inviteEmail) && isNumeric(invitePermission)
      );
    } else {
      setCanSubmit(
        isNameValid(inviteName) &&
          isEmailValid(inviteEmail) &&
          inviteTeams.length > 0 &&
          isNumeric(invitePermission) &&
          invitePermission > 0
      );
    }
  }, [
    _adminPermissionId,
    inviteName,
    inviteEmail,
    invitePermission,
    inviteTeams,
    isManageAdminUsersPage,
    pageType,
  ]);

  const onUpdateEmail = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value === '') setEmailIsInvalid(false);
    setInviteEmail(value);
  };

  const emailOnBlur = (): void => {
    if (inviteEmail !== '') setEmailIsInvalid(!isEmailValid(inviteEmail));
  };

  const onUpdateName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value === '') setNameIsInvalid(false);
    setInviteName(e.target.value);
  };

  const nameOnBlur = (): void => {
    if (inviteName !== '') setNameIsInvalid(!isNameValid(inviteName));
  };

  const onSendInvite = (): void => {
    if (pageType === 'ManageTeam') {
      inviteConsultee(inviteRequest, {
        onSuccess: () => {
          resetInviteState();
        },
      });
    } else setShowInviteUserModal(true);
  };
  return {
    emailIsInvalid,
    nameIsInvalid,
    canSubmit,
    onUpdateEmail,
    emailOnBlur,
    onUpdateName,
    nameOnBlur,
    onSendInvite,
    inviteName,
    inviteEmail,
    inviteTeams,
    invitePermission,
  };
};
